import { memo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { useCalendarGridStore, useNonReactiveStore } from '../../store/calendar-grid.store';
import { ColumnHeaderOverlayStyleFn } from '../../types';
import { CalendarDragStateContext, defaultCalendarDragState } from '../calendar-drag-state-context';
import { ColumnHeader } from './column-header';

const MIN_DRAG_DISTANCE = 5;

const resolveColumnGroup = (
  columnId: string,
  groupIds: string[],
  groupsById: Record<string, { columnIds: string[] } | undefined>,
  localColumnOrderByGroup: Record<string, string[] | undefined>
): { groupId: string; columnIds: string[] } | undefined => {
  for (const groupId of groupIds) {
    const columnIds = localColumnOrderByGroup[groupId] ?? groupsById[groupId]?.columnIds ?? [];
    if (columnIds.includes(columnId)) return { groupId, columnIds };
  }
  return undefined;
};

const ColumnGroupSectionsDndWrapperComponent = ({ children }: { children: React.ReactNode }) => {
  const { headerType, isColumnReorderEnabled, setLocalColumnOrder, clearLocalColumnOrder, setIsColumnReorderDragging } =
    useCalendarGridStore([
      'headerType',
      'isColumnReorderEnabled',
      'setLocalColumnOrder',
      'clearLocalColumnOrder',
      'setIsColumnReorderDragging',
    ]);
  const dragStyles = useCalendarGridStore((s) => s.dragStyles);

  const nonReactiveStore = useNonReactiveStore();
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const activeGroupIdRef = useRef<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: MIN_DRAG_DISTANCE } }));

  const handleDragStart = ({ active }: DragStartEvent) => {
    const activeId = active.id as string;
    const { groupIds: gIds, groupsById, localColumnOrderByGroup } = nonReactiveStore.getState();
    const resolved = resolveColumnGroup(activeId, gIds, groupsById, localColumnOrderByGroup);
    const groupId = resolved?.groupId;
    activeGroupIdRef.current = groupId ?? null;
    setActiveDragId(activeId);
    setIsColumnReorderDragging(true);
    if (groupId) {
      const currentOrder = resolved?.columnIds ?? [];
      setLocalColumnOrder(groupId, [...currentOrder]);
    }
  };

  const handleDragOver = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !activeGroupIdRef.current) return;
    const { groupIds: gIds, groupsById, localColumnOrderByGroup } = nonReactiveStore.getState();
    const resolved = resolveColumnGroup(over.id as string, gIds, groupsById, localColumnOrderByGroup);
    const overGroupId = resolved?.groupId;
    // Prevent cross-group drops
    if (overGroupId !== activeGroupIdRef.current) return;
    const columnIds = resolved?.columnIds ?? [];
    const oldIndex = columnIds.indexOf(active.id as string);
    const newIndex = columnIds.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      setLocalColumnOrder(activeGroupIdRef.current, arrayMove(columnIds, oldIndex, newIndex));
    }
  };

  const handleDragEnd = () => {
    const groupId = activeGroupIdRef.current;
    activeGroupIdRef.current = null;
    setActiveDragId(null);
    if (!groupId) return;

    const { localColumnOrderByGroup, groupsById, onColumnReorder } = nonReactiveStore.getState();
    const localOrder = localColumnOrderByGroup[groupId];
    const originalOrder = groupsById[groupId]?.columnIds ?? [];

    // Commit only if the order actually changed — covers the case where over.id === active.id
    // after array-based reordering during onDragOver
    if (localOrder && localOrder.join(',') !== originalOrder.join(',')) {
      onColumnReorder?.(groupId, localOrder);
    }

    // Do NOT clear localColumnOrderByGroup or re-enable scroll snap immediately.
    // The consumer's onColumnReorder callback triggers state updates that propagate
    // through CalendarGridProvider → CalendarGridStoreSync (a useEffect, so post-paint).
    // If we clear and re-enable in this same cycle, scrollSnapType fires on the OLD layout.
    // Two rAFs ensure we wait past: render cycle (1st rAF) + useEffect post-paint (2nd rAF).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clearLocalColumnOrder(groupId);
        setIsColumnReorderDragging(false);
      });
    });
  };

  const handleDragCancel = () => {
    if (activeGroupIdRef.current) clearLocalColumnOrder(activeGroupIdRef.current);
    activeGroupIdRef.current = null;
    setActiveDragId(null);
    setIsColumnReorderDragging(false);
  };

  if (!isColumnReorderEnabled) return <>{children}</>;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={{ enabled: false, layoutShiftCompensation: false }}
    >
      {children}
      {createPortal(
        <DragOverlay>
          {activeDragId && (
            <div
              css={[
                defaultColumnHeaderDragOverlayStyle,
                typeof dragStyles.columnHeaderDragOverlay === 'function'
                  ? (dragStyles.columnHeaderDragOverlay as ColumnHeaderOverlayStyleFn)(activeDragId)
                  : dragStyles.columnHeaderDragOverlay,
              ]}
            >
              {/* Drag handle visible in the overlay — mirrors SortableColumnHeader layout */}
              <div css={overlayDragHandleStyle}>
                <Icon name='dragand-drop-handle' size={20} color='subdued' />
              </div>
              <CalendarDragStateContext.Provider
                value={{ ...defaultCalendarDragState, isColumnHeaderDragOverlay: true }}
              >
                <div css={overlayContentStyle}>
                  <ColumnHeader
                    columnId={activeDragId}
                    headerType={headerType}
                    groupId={activeGroupIdRef.current ?? ''}
                  />
                </div>
              </CalendarDragStateContext.Provider>
            </div>
          )}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

export const ColumnGroupSectionsDndWrapper = memo(ColumnGroupSectionsDndWrapperComponent);

// Mirrors SortableColumnHeader's dragHandleStyle inside the drag overlay
const overlayDragHandleStyle = css({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  cursor: 'grabbing',
  paddingLeft: theme.spacing(0.5),
});

// Mirrors SortableColumnHeader's contentStyle inside the drag overlay
const overlayContentStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 0,
  flex: 1,
  padding: theme.spacing(0, 2.5),
  width: '100%',
});

/**
 * Default drag overlay styles matching the existing timeline column header implementation:
 * rotate -10deg, floating shadow, white background, rounded corners, blue left border.
 * Consumers can override via dragStyles.columnHeaderDragOverlay.
 */
const defaultColumnHeaderDragOverlayStyle = css({
  display: 'flex',
  alignItems: 'center',
  transform: 'rotate(-10deg)',
  boxShadow: theme.shadows.floating,
  borderRadius: theme.borderRadius.medium,
  backgroundColor: theme.colors.white,
  padding: theme.spacing(0.5),
  borderLeft: `4px solid ${theme.colors.primary50}`,
  height: 'auto',
  cursor: 'grabbing',
});
